import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1, locale: 'en-US' },
  { code: 'EUR', symbol: '€', name: 'Euro', rate: 0.92, locale: 'de-DE' },
  { code: 'GBP', symbol: '£', name: 'British Pound', rate: 0.79, locale: 'en-GB' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', rate: 3.67, locale: 'ar-AE' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', rate: 83.5, locale: 'en-IN' },
  { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal', rate: 3.75, locale: 'ar-SA' },
  { code: 'CHF', symbol: 'CHF ', name: 'Swiss Franc', rate: 0.88, locale: 'de-CH' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', rate: 149.5, locale: 'ja-JP' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', rate: 7.24, locale: 'zh-CN' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', rate: 1.34, locale: 'en-SG' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', rate: 1.53, locale: 'en-AU' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', rate: 1.36, locale: 'en-CA' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', rate: 7.82, locale: 'en-HK' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won', rate: 1320, locale: 'ko-KR' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht', rate: 35.2, locale: 'th-TH' },
  { code: 'MYR', symbol: 'RM ', name: 'Malaysian Ringgit', rate: 4.72, locale: 'ms-MY' },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal', rate: 3.64, locale: 'ar-QA' },
  { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar', rate: 0.31, locale: 'ar-KW' },
  { code: 'BHD', symbol: 'BD ', name: 'Bahraini Dinar', rate: 0.377, locale: 'ar-BH' },
  { code: 'OMR', symbol: 'ر.ع', name: 'Omani Rial', rate: 0.385, locale: 'ar-OM' },
];

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [code, setCode] = useState(() => {
    try { return sessionStorage.getItem('vwatch_currency') || 'USD'; }
    catch (e) { console.warn('Currency read error:', e.message); return 'USD'; }
  });

  const currency = useMemo(() => CURRENCIES.find(c => c.code === code) || CURRENCIES[0], [code]);

  const setCurrency = useCallback((newCode) => {
    setCode(newCode);
    try { sessionStorage.setItem('vwatch_currency', newCode); } catch (e) { console.warn('Currency save error:', e.message); }
  }, []);

  const convert = useCallback((usdAmount) => {
    if (!usdAmount) return 0;
    return Math.round(usdAmount * currency.rate);
  }, [currency.rate]);

  const format = useCallback((usdAmount) => {
    const converted = convert(usdAmount);
    const locale = currency.locale;
    const sym = currency.symbol;

    // Indian numbering: lakhs and crores
    if (currency.code === 'INR') {
      if (converted >= 10000000) return `${sym}${(converted / 10000000).toFixed(2)} Cr`;
      if (converted >= 100000) return `${sym}${(converted / 100000).toFixed(2)} L`;
      if (converted >= 1000) return `${sym}${(converted / 1000).toFixed(1)}K`;
      return `${sym}${converted.toLocaleString('en-IN')}`;
    }

    // Japanese Yen / Korean Won — large numbers, no decimals
    if (currency.code === 'JPY' || currency.code === 'KRW') {
      if (converted >= 100000000) return `${sym}${(converted / 100000000).toFixed(1)}億`;
      if (converted >= 10000) return `${sym}${(converted / 10000).toFixed(0)}万`;
      return `${sym}${converted.toLocaleString(locale)}`;
    }

    // Arabic currencies
    if (['AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR'].includes(currency.code)) {
      if (converted >= 1000000) return `${sym} ${(converted / 1000000).toFixed(2)}M`;
      if (converted >= 1000) return `${sym} ${(converted / 1000).toFixed(0)}K`;
      return `${sym} ${converted.toLocaleString('en-US')}`;
    }

    // Western currencies
    if (converted >= 1000000) return `${sym}${(converted / 1000000).toFixed(1)}M`;
    if (converted >= 1000) return `${sym}${(converted / 1000).toFixed(0)}k`;
    return `${sym}${converted.toLocaleString(locale)}`;
  }, [convert, currency]);

  const formatFull = useCallback((usdAmount) => {
    const converted = convert(usdAmount);
    const sym = currency.symbol;

    // Indian numbering system (XX,XX,XXX)
    if (currency.code === 'INR') {
      return `${sym}${converted.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }

    // Arabic currencies with space
    if (['AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR'].includes(currency.code)) {
      return `${sym} ${converted.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }

    return `${sym}${converted.toLocaleString(currency.locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }, [convert, currency]);

  const value = useMemo(() => ({
    code: currency.code, symbol: currency.symbol, name: currency.name, rate: currency.rate,
    currencies: CURRENCIES, setCurrency, convert, format, formatFull,
  }), [currency, setCurrency, convert, format, formatFull]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => useContext(CurrencyContext);
